import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  MapPin,
  Ticket,
  ExternalLink,
  X,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Skeleton,
  Label,
  Textarea,
} from '@retreatflow360/ui';
import { formatDate, formatCurrency, useToast } from '@retreatflow360/ui';
import { api } from '@/lib/api';

interface Booking {
  id: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'refunded' | 'waitlisted';
  pricingTier: string;
  baseAmount: number;
  currency: string;
  createdAt: string;
  confirmedAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  dietaryNotes?: string;
  accessibilityNotes?: string;
  event?: {
    id: string;
    title: string;
    slug: string;
    startDate: string;
    endDate: string;
    venue?: {
      name: string;
      city: string | null;
      country: string | null;
    } | null;
  };
}

const statusConfig: Record<
  string,
  { color: 'default' | 'secondary' | 'destructive' | 'success' | 'warning'; icon: typeof CheckCircle; label: string }
> = {
  confirmed: { color: 'success', icon: CheckCircle, label: 'Confirmed' },
  pending: { color: 'warning', icon: Clock, label: 'Pending' },
  cancelled: { color: 'destructive', icon: XCircle, label: 'Cancelled' },
  refunded: { color: 'secondary', icon: XCircle, label: 'Refunded' },
  waitlisted: { color: 'default', icon: Clock, label: 'Waitlisted' },
};

export default function BookingsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-bookings'],
    queryFn: async () => {
      const response = await api.get('/api/v1/bookings/me');
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error?.message || 'Failed to fetch bookings');
      }
      const result = await response.json();
      return (result.data?.bookings || result.bookings || []) as Booking[];
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: string; reason: string }) => {
      const response = await api.post(`/api/v1/bookings/me/${bookingId}/cancel`, { reason });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error?.message || 'Failed to cancel booking');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      setShowCancelDialog(false);
      setSelectedBooking(null);
      setCancelReason('');
      toast({
        title: 'Booking cancelled',
        description: 'Your booking has been cancelled successfully.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Cancellation failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const bookings = data || [];

  const upcomingBookings = bookings.filter(
    (b) =>
      b.event &&
      new Date(b.event.startDate) > new Date() &&
      ['confirmed', 'pending'].includes(b.status)
  );
  const pastBookings = bookings.filter(
    (b) => b.event && new Date(b.event.endDate) < new Date() && b.status !== 'cancelled'
  );
  const cancelledBookings = bookings.filter((b) => b.status === 'cancelled' || b.status === 'refunded');

  const handleCancelBooking = () => {
    if (!selectedBooking) return;
    cancelMutation.mutate({
      bookingId: selectedBooking.id,
      reason: cancelReason || 'Cancelled by user',
    });
  };

  const BookingCard = ({ booking }: { booking: Booking }) => {
    const config = statusConfig[booking.status] || statusConfig.pending;
    const StatusIcon = config.icon;
    const isUpcoming = booking.event && new Date(booking.event.startDate) > new Date();
    const canCancel = ['pending', 'confirmed'].includes(booking.status) && isUpcoming;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
      >
        <Card
          className="cursor-pointer overflow-hidden transition-shadow hover:shadow-md"
          onClick={() => setSelectedBooking(booking)}
        >
          <CardContent className="p-0">
            <div className="flex flex-col sm:flex-row">
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant={config.color} className="flex items-center gap-1">
                      <StatusIcon className="h-3 w-3" />
                      {config.label}
                    </Badge>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>

                <h3 className="mt-2 font-semibold">{booking.event?.title || 'Unknown Event'}</h3>

                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                  {booking.event && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {formatDate(booking.event.startDate)} - {formatDate(booking.event.endDate)}
                      </span>
                    </div>
                  )}
                  {booking.event?.venue && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>
                        {booking.event.venue.name}
                        {booking.event.venue.city && `, ${booking.event.venue.city}`}
                        {booking.event.venue.country && `, ${booking.event.venue.country}`}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Ticket className="h-4 w-4" />
                    <span>{booking.pricingTier}</span>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                  {booking.event && (
                    <Button size="sm" variant="outline" asChild>
                      <Link to={`/events/${booking.event.slug}`}>
                        <ExternalLink className="mr-2 h-4 w-4" />
                        View Event
                      </Link>
                    </Button>
                  )}
                  {canCancel && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        setSelectedBooking(booking);
                        setShowCancelDialog(true);
                      }}
                    >
                      Cancel Booking
                    </Button>
                  )}
                </div>
              </div>

              <div className="border-t bg-muted/30 p-4 sm:border-l sm:border-t-0 sm:w-32">
                <div className="text-center sm:text-right">
                  <span className="text-xs text-muted-foreground">Total</span>
                  <p className="text-lg font-bold">
                    {formatCurrency(booking.baseAmount, booking.currency)}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="mb-6 h-10 w-48" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="py-12 text-center">
          <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-destructive" />
          <h3 className="text-lg font-semibold">Failed to load bookings</h3>
          <p className="mt-2 text-muted-foreground">
            {error instanceof Error ? error.message : 'Please try again later'}
          </p>
          <Button
            className="mt-4"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['my-bookings'] })}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="mb-6 text-3xl font-bold">My Bookings</h1>

        <Tabs defaultValue="upcoming">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming ({upcomingBookings.length})</TabsTrigger>
            <TabsTrigger value="past">Past ({pastBookings.length})</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled ({cancelledBookings.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-6">
            {upcomingBookings.length ? (
              <div className="space-y-4">
                <AnimatePresence mode="popLayout">
                  {upcomingBookings.map((booking) => (
                    <BookingCard key={booking.id} booking={booking} />
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <EmptyState
                icon={Ticket}
                title="No upcoming bookings"
                description="Find your next retreat experience"
                action={
                  <Button asChild className="mt-4">
                    <Link to="/events">Browse Events</Link>
                  </Button>
                }
              />
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-6">
            {pastBookings.length ? (
              <div className="space-y-4">
                {pastBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Calendar}
                title="No past bookings"
                description="Your completed retreats will appear here"
              />
            )}
          </TabsContent>

          <TabsContent value="cancelled" className="mt-6">
            {cancelledBookings.length ? (
              <div className="space-y-4">
                {cancelledBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <EmptyState icon={Ticket} title="No cancelled bookings" />
            )}
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Booking Detail Modal */}
      <AnimatePresence>
        {selectedBooking && !showCancelDialog && (
          <BookingDetailModal
            booking={selectedBooking}
            onClose={() => setSelectedBooking(null)}
            onCancelClick={() => setShowCancelDialog(true)}
          />
        )}
      </AnimatePresence>

      {/* Cancel Confirmation Dialog */}
      <AnimatePresence>
        {showCancelDialog && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowCancelDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-md rounded-lg bg-background p-6 shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                </div>
                <h2 className="text-lg font-semibold">Cancel Booking</h2>
              </div>

              <p className="mb-4 text-sm text-muted-foreground">
                Are you sure you want to cancel your booking for{' '}
                <strong>{selectedBooking.event?.title}</strong>? This action cannot be undone.
              </p>

              <div className="mb-4 space-y-2">
                <Label htmlFor="cancelReason">Reason for cancellation (optional)</Label>
                <Textarea
                  id="cancelReason"
                  placeholder="Let us know why you're cancelling..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowCancelDialog(false);
                    setCancelReason('');
                  }}
                >
                  Keep Booking
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleCancelBooking}
                  disabled={cancelMutation.isPending}
                >
                  {cancelMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Cancel Booking'
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: typeof Ticket;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="py-12 text-center">
      <Icon className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
      <h3 className="text-lg font-semibold">{title}</h3>
      {description && <p className="mt-2 text-muted-foreground">{description}</p>}
      {action}
    </div>
  );
}

function BookingDetailModal({
  booking,
  onClose,
  onCancelClick,
}: {
  booking: Booking;
  onClose: () => void;
  onCancelClick: () => void;
}) {
  const config = statusConfig[booking.status] || statusConfig.pending;
  const StatusIcon = config.icon;
  const isUpcoming = booking.event && new Date(booking.event.startDate) > new Date();
  const canCancel = ['pending', 'confirmed'].includes(booking.status) && isUpcoming;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between border-b bg-background p-4">
          <h2 className="text-lg font-semibold">Booking Details</h2>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Status */}
          <div className="flex items-center gap-2">
            <Badge variant={config.color} className="flex items-center gap-1 text-sm">
              <StatusIcon className="h-4 w-4" />
              {config.label}
            </Badge>
          </div>

          {/* Event Info */}
          <div>
            <h3 className="text-xl font-semibold">{booking.event?.title}</h3>
            <div className="mt-3 space-y-2 text-sm text-muted-foreground">
              {booking.event && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {formatDate(booking.event.startDate)} - {formatDate(booking.event.endDate)}
                  </span>
                </div>
              )}
              {booking.event?.venue && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {booking.event.venue.name}
                    {booking.event.venue.city && `, ${booking.event.venue.city}`}
                    {booking.event.venue.country && `, ${booking.event.venue.country}`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Pricing */}
          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Ticket Type</span>
              <span className="font-medium">{booking.pricingTier}</span>
            </div>
            <div className="mt-2 flex justify-between border-t pt-2">
              <span className="font-semibold">Total</span>
              <span className="font-bold text-lg">
                {formatCurrency(booking.baseAmount, booking.currency)}
              </span>
            </div>
          </div>

          {/* Timeline */}
          <div className="space-y-3">
            <h4 className="font-medium">Booking Timeline</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span className="text-muted-foreground">Booked on</span>
                <span>{formatDate(booking.createdAt)}</span>
              </div>
              {booking.confirmedAt && (
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <span className="text-muted-foreground">Confirmed on</span>
                  <span>{formatDate(booking.confirmedAt)}</span>
                </div>
              )}
              {booking.cancelledAt && (
                <div className="flex items-center gap-3">
                  <div className="h-2 w-2 rounded-full bg-destructive" />
                  <span className="text-muted-foreground">Cancelled on</span>
                  <span>{formatDate(booking.cancelledAt)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Cancellation Reason */}
          {booking.cancellationReason && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
              <h4 className="font-medium text-destructive">Cancellation Reason</h4>
              <p className="mt-1 text-sm text-muted-foreground">{booking.cancellationReason}</p>
            </div>
          )}

          {/* Notes */}
          {(booking.dietaryNotes || booking.accessibilityNotes) && (
            <div className="space-y-3">
              <h4 className="font-medium">Your Notes</h4>
              {booking.dietaryNotes && (
                <div>
                  <span className="text-sm text-muted-foreground">Dietary Requirements:</span>
                  <p className="text-sm">{booking.dietaryNotes}</p>
                </div>
              )}
              {booking.accessibilityNotes && (
                <div>
                  <span className="text-sm text-muted-foreground">Accessibility Needs:</span>
                  <p className="text-sm">{booking.accessibilityNotes}</p>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            {booking.event && (
              <Button variant="outline" className="flex-1" asChild>
                <Link to={`/events/${booking.event.slug}`}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Event
                </Link>
              </Button>
            )}
            {canCancel && (
              <Button
                variant="destructive"
                className="flex-1"
                onClick={onCancelClick}
              >
                Cancel Booking
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
